import React, { useEffect, useRef, useState } from 'react';
import createGlobe from 'cobe';
import type { Arc, Globe as Cobe, Marker } from 'cobe';
import { usePrefersReducedMotion } from '../theme/hooks';

export type GlobePoint = { id: string; lat: number; lng: number; weight: number; title: string; sub: string; arc: boolean };
export type GlobeHome = { lat: number; lng: number; label: string };
type RGB = [number, number, number];

const PI = Math.PI;
const clamp = (v: number, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, v));
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);
const easeInOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const css = (name: string) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();
/** '#78be20' or 'rgba(…)' as cobe's 0–1 RGB. */
const rgb = (c: string, fallback: RGB): RGB => {
  if (c.startsWith('#')) {
    const hex = c.length === 4 ? c.slice(1).split('').map((x) => x + x).join('') : c.slice(1, 7);
    const n = parseInt(hex, 16);
    return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
  }
  const m = c.match(/[\d.]+/g);
  return m && m.length >= 3 ? [Number(m[0]) / 255, Number(m[1]) / 255, Number(m[2]) / 255] : fallback;
};
const mix = (a: RGB, b: RGB, t: number): RGB => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];

// cobe's own lat/lng → unit vector, so overlay positions match its shader exactly.
const toVec = (lat: number, lng: number): RGB => {
  const r = (lat * PI) / 180;
  const a = (lng * PI) / 180 - PI;
  const o = Math.cos(r);
  return [-o * Math.cos(a), Math.sin(r), o * Math.sin(a)];
};
/** phi/theta that bring (lat, lng) to the middle of the globe. */
const facing = (lat: number, lng: number): [number, number] => [PI - ((lng * PI) / 180 - PI / 2), (lat * PI) / 180];

type View = { w: number; h: number; cx: number; cy: number; R: number };
/** Wide cards put the globe right of centre, big enough to run past the edges; phones centre it under the headline. */
const viewOf = (w: number, h: number): View => {
  if (w >= 640) return { w, h, cx: w * 0.6, cy: h * 0.56, R: Math.min(w * 0.42, h * 0.52) };
  // Tablet widths: higher and further right, clear of the town list in the bottom-left.
  if (w >= 440) return { w, h, cx: w * 0.64, cy: h * 0.42, R: Math.min(w * 0.4, h * 0.34) };
  const R = Math.min(w * 0.46, h * 0.3);
  return { w, h, cx: w / 2, cy: 150 + R * 0.9, R };
};
// cobe draws the sphere with radius 0.4·scale·height, centred on the canvas moved by scale·offset/2.
const frameOf = (v: View) => {
  const scale = v.R / (0.4 * v.h);
  return { scale, offset: [(2 * (v.cx - v.w / 2)) / scale, (2 * (v.cy - v.h / 2)) / scale] as [number, number] };
};
const MARKER_LIFT = 0.02;
/** Screen position of a point, from cobe's marker vertex shader. */
const project = (lat: number, lng: number, phi: number, theta: number, v: View) => {
  const [ux, uy, uz] = toVec(lat, lng);
  const m = 0.8 + MARKER_LIFT;
  const [ax, ay, az] = [ux * m, uy * m, uz * m];
  const c = Math.cos(theta);
  const d = Math.sin(theta);
  const e = Math.cos(phi);
  const f = Math.sin(phi);
  const lx = e * ax + f * az;
  const ly = f * d * ax + c * ay - e * d * az;
  const lz = -f * c * ax + d * ay + e * c * az;
  const k = v.R / 0.8;
  return { x: v.cx + lx * k, y: v.cy - ly * k, visible: !(lz < 0 && Math.hypot(lx, ly) < 0.8) };
};
const markerSize = (w: number) => 0.016 + 0.034 * Math.sqrt(w);

const paletteOf = () => {
  const light = document.documentElement.getAttribute('data-oc-admin') === 'light';
  const acc = rgb(css('--oa-accent'), [0.47, 0.75, 0.13]);
  const text = rgb(css('--oa-text'), [0.93, 0.95, 0.92]);
  return light
    ? { dark: 0, diffuse: 1.5, mapBrightness: 4, baseColor: [1, 1, 1] as RGB, glowColor: mix([1, 1, 1], acc, 0.2), acc, text, arc: mix(acc, [1, 1, 1], 0.15) }
    : { dark: 1, diffuse: 1.2, mapBrightness: 6, baseColor: [0.32, 0.37, 0.33] as RGB, glowColor: mix([0.06, 0.08, 0.06], acc, 0.3), acc, text, arc: mix(acc, [0.1, 0.12, 0.1], 0.25) };
};

/**
 * A cobe (WebGL) dotted globe: visitor towns as markers sized by visits, arcs from home to the
 * towns further than a short drive, and home as a light dot. Drag turns it, with inertia (touch
 * turns longitude only, so the page still scrolls); hovering a town, or naming one in `focus`,
 * turns to it and shows its figures in a pill. It renders only while something moves: the
 * entrance, a drag, a turn, or a highlighted town.
 */
export const Globe: React.FC<{ points: GlobePoint[]; home: GlobeHome; focus: string | null }> = ({ points, home, focus }) => {
  const stage = useRef<HTMLDivElement>(null);
  const cv = useRef<HTMLCanvasElement>(null);
  const tip = useRef<HTMLDivElement>(null);
  const tag = useRef<HTMLDivElement>(null);
  const reduce = usePrefersReducedMotion();
  const [hover, setHover] = useState<string | null>(null);
  const active = hover ?? focus;
  const activePoint = points.find((p) => p.id === active) || null;

  const st = useRef({
    phi: 0,
    theta: 0.3,
    vel: [0, 0] as [number, number],
    drag: null as null | { x: number; y: number; touch: boolean },
    tween: null as null | { from: [number, number]; to: [number, number]; t0: number; dur: number },
    t0: 0,
    started: false,
    view: viewOf(1, 1),
    globe: null as Cobe | null,
    pal: null as null | ReturnType<typeof paletteOf>,
    points,
    home,
    active: null as string | null,
    raf: 0,
    kick: () => {},
  });
  st.current.points = points;
  st.current.home = home;
  st.current.active = active;

  useEffect(() => {
    const el = cv.current;
    const box = stage.current;
    if (!el || !box) return undefined;
    const s = st.current;
    s.pal = paletteOf();
    s.view = viewOf(box.offsetWidth || 1, box.offsetHeight || 1);
    const pal = s.pal;
    const f0 = frameOf(s.view);
    s.globe = createGlobe(el, {
      devicePixelRatio: Math.min(2, window.devicePixelRatio || 1),
      width: s.view.w,
      height: s.view.h,
      phi: s.phi,
      theta: s.theta,
      dark: pal.dark,
      diffuse: pal.diffuse,
      mapSamples: 20000,
      mapBrightness: pal.mapBrightness,
      baseColor: pal.baseColor,
      markerColor: pal.acc,
      glowColor: pal.glowColor,
      arcColor: pal.arc,
      arcWidth: 0.9,
      arcHeight: 0,
      markerElevation: MARKER_LIFT,
      scale: f0.scale,
      offset: f0.offset,
      markers: [],
      arcs: [],
    });

    const frame = (now: number) => {
      s.raf = 0;
      const g = s.globe;
      const p = s.pal;
      if (!g || !p) return;
      if (s.tween) {
        const k = clamp((now - s.tween.t0) / s.tween.dur);
        const e = easeInOut(k);
        s.phi = s.tween.from[0] + (s.tween.to[0] - s.tween.from[0]) * e;
        s.theta = s.tween.from[1] + (s.tween.to[1] - s.tween.from[1]) * e;
        if (k >= 1) s.tween = null;
      } else if (!s.drag && (Math.abs(s.vel[0]) > 0.0002 || Math.abs(s.vel[1]) > 0.0002)) {
        s.phi += s.vel[0];
        s.theta = clamp(s.theta + s.vel[1], -1.2, 1.2);
        s.vel = [s.vel[0] * 0.93, s.vel[1] * 0.93];
      }

      const since = now - s.t0;
      const grow = (i: number) => (reduce ? 1 : easeOut(clamp((since - 600 - i * 45) / 500)));
      const on = s.active;
      const markers: Marker[] = s.points.map((pt, i) => ({
        location: [pt.lat, pt.lng],
        size: markerSize(pt.weight) * (on === pt.id ? 1.45 : 1) * grow(i),
        color: on === pt.id ? p.text : p.acc,
      }));
      markers.push({ location: [s.home.lat, s.home.lng], size: 0.022 * grow(0), color: p.text });
      const arcs: Arc[] = s.points.filter((pt) => pt.arc).map((pt) => ({ from: [s.home.lat, s.home.lng], to: [pt.lat, pt.lng], color: on === pt.id ? p.text : p.arc }));
      const fr = frameOf(s.view);
      g.update({ phi: s.phi, theta: s.theta, scale: fr.scale, offset: fr.offset, markers, arcs, arcHeight: 0.22 * (reduce ? 1 : easeOut(clamp((since - 900) / 1100))) });

      // Pills follow their points while the globe turns.
      const place = (node: HTMLDivElement | null, lat: number, lng: number, lift: number) => {
        if (!node) return;
        const q = project(lat, lng, s.phi, s.theta, s.view);
        node.style.opacity = q.visible ? '1' : '0';
        node.style.left = `${clamp(q.x, 100, s.view.w - 100)}px`;
        node.style.top = `${q.y - lift}px`;
      };
      const ap = on ? s.points.find((q) => q.id === on) : null;
      if (ap) place(tip.current, ap.lat, ap.lng, (markerSize(ap.weight) * 1.45 * s.view.R) / 0.8 + 6);
      else if (tip.current) tip.current.style.opacity = '0';
      place(tag.current, s.home.lat, s.home.lng, -14);

      const moving = !!s.tween || !!s.drag || Math.abs(s.vel[0]) > 0.0002 || Math.abs(s.vel[1]) > 0.0002 || (!reduce && s.started && since < 2400);
      if (moving) s.raf = requestAnimationFrame(frame);
    };
    s.kick = () => {
      if (!s.raf) s.raf = requestAnimationFrame(frame);
    };

    const ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            s.view = viewOf(box.offsetWidth || 1, box.offsetHeight || 1);
            s.globe?.update({ width: s.view.w, height: s.view.h });
            s.kick();
          })
        : undefined;
    ro?.observe(box);
    // Light/dark switch: recolour.
    const mo = new MutationObserver(() => {
      s.pal = paletteOf();
      const q = s.pal;
      s.globe?.update({ dark: q.dark, diffuse: q.diffuse, mapBrightness: q.mapBrightness, baseColor: q.baseColor, markerColor: q.acc, glowColor: q.glowColor, arcColor: q.arc });
      s.kick();
    });
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-oc-admin'] });
    s.kick();
    return () => {
      cancelAnimationFrame(s.raf);
      s.raf = 0;
      ro?.disconnect();
      mo.disconnect();
      s.globe?.destroy();
      s.globe = null;
    };
  }, [reduce]);

  // First data: turn to face the visitors (weighted by visits, tipped so they sit high).
  useEffect(() => {
    const s = st.current;
    if (s.started || !points.length) return;
    s.started = true;
    let x = 0;
    let y = 0;
    let z = 0;
    for (const p of [...points, { ...home, weight: 1 }]) {
      const [a, b, c] = toVec(p.lat, p.lng);
      x += a * p.weight;
      y += b * p.weight;
      z += c * p.weight;
    }
    const lat = (Math.atan2(y, Math.hypot(x, z)) * 180) / PI;
    const lng = ((Math.atan2(z, -x) + PI) * 180) / PI;
    const [phi, theta] = facing(lat, lng);
    const to: [number, number] = [phi, clamp(theta - 0.22, -1.2, 1.2)];
    s.t0 = performance.now();
    if (reduce) [s.phi, s.theta] = to;
    else s.tween = { from: [to[0] + 1.8, to[1]], to, t0: s.t0, dur: 1800 };
    s.kick();
  }, [points, home, reduce]);

  // A town named from outside (the list) turns to face it, the shortest way round.
  useEffect(() => {
    const s = st.current;
    const p = points.find((q) => q.id === focus);
    if (p) {
      const [phi, theta] = facing(p.lat, p.lng);
      let to0 = phi;
      while (to0 - s.phi > PI) to0 -= 2 * PI;
      while (s.phi - to0 > PI) to0 += 2 * PI;
      const to: [number, number] = [to0, clamp(theta - 0.15, -1.2, 1.2)];
      if (reduce) [s.phi, s.theta] = to;
      else s.tween = { from: [s.phi, s.theta], to, t0: performance.now(), dur: 900 };
      s.vel = [0, 0];
    }
    s.kick();
  }, [focus, points, reduce]);

  useEffect(() => st.current.kick(), [points, active]);

  const onDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const s = st.current;
    s.drag = { x: e.clientX, y: e.clientY, touch: e.pointerType === 'touch' };
    s.tween = null;
    s.vel = [0, 0];
    e.currentTarget.setPointerCapture(e.pointerId);
    s.kick();
  };
  const onMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const s = st.current;
    if (s.drag) {
      const dx = e.clientX - s.drag.x;
      const dy = s.drag.touch ? 0 : e.clientY - s.drag.y;
      s.drag.x = e.clientX;
      s.drag.y = e.clientY;
      const k = 1 / s.view.R;
      s.phi += dx * k;
      s.theta = clamp(s.theta + dy * k, -1.2, 1.2);
      s.vel = [dx * k, dy * k];
      s.kick();
      return;
    }
    const r = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    let best: string | null = null;
    let bestD = Infinity;
    for (const p of s.points) {
      const q = project(p.lat, p.lng, s.phi, s.theta, s.view);
      if (!q.visible) continue;
      const d = Math.hypot(q.x - x, q.y - y);
      if (d <= (markerSize(p.weight) * s.view.R) / 0.8 + 6 && d < bestD) {
        best = p.id;
        bestD = d;
      }
    }
    if (best !== hover) setHover(best);
  };
  const onUp = () => {
    st.current.drag = null;
    st.current.kick();
  };

  return (
    <div ref={stage} className="absolute inset-0">
      <canvas
        ref={cv}
        className="block h-full w-full cursor-grab touch-pan-y active:cursor-grabbing"
        role="img"
        aria-label="Globe of the towns visits came from, with arcs from the showroom"
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
        onPointerLeave={() => setHover(null)}
      />
      <div ref={tag} className="oa-geo-tag" style={{ opacity: 0 }}>
        {home.label}
      </div>
      <div ref={tip} className="oa-tip" style={{ opacity: 0, transform: 'translate(-50%, -100%)', transition: 'opacity .2s' }}>
        {activePoint ? (
          <>
            {activePoint.title}
            <small>{activePoint.sub}</small>
          </>
        ) : null}
      </div>
    </div>
  );
};
